// src/components/Replan/ShapeList.js
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  getDisplayMeasurement,
  getDisplayAreaMeasurement,
  calculateDistance,
  resolveShapeScaleProps,
} from '../Replan/utils/drawingUtils';
import { getShapeMeasurementData } from '../Replan/utils/measurementUtils'; // Import the new utility
import { TOOLS } from '../Replan/constants/drawingTools';
import {
  DISPLAY_KEY_SEGMENT_LENGTH,
  DISPLAY_KEY_AREA,
  DISPLAY_KEY_MEASUREMENT,
} from '../Replan/shapes/ShapeRenderer';
import AssemblyManager from '../AssemblyManager/AssemblyManager';

import DraggableShapeListItem from './DraggableShapeListItem';
import { saveAssemblyFromShape } from './assemblyUtils';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  DragOverlay,
  pointerWithin,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DroppableGroup from './DroppableGroup';

import { ReactComponent as SidebarClose } from '../../svg/arrow-left-svgrepo-com.svg';
import { ReactComponent as SidebarOpen } from '../../svg/arrow-right-svgrepo-com.svg';
import { ReactComponent as SettingsIcon } from '../../svg/settings.svg';
import { ReactComponent as TagIcon } from '../../svg/tag.svg';

// Built-in grouping key for grouping by pageInfo
const GROUP_BY_PAGE_KEY = '__page__';

const ShapeList = ({
  projectName,
  organization: organizationProp,
  shapes,
  selectedShapeIds,
  onShapeSelect,
  onUpdateShape,
  onReorderShapes,
  currentScaleProps,
  dpi,
  imageSize,
  authUser,
  firebase,
  storageKeyPrefix = '',
  groupingKey: groupingKeyProp,
  onGroupingKeyChange,
  displayKeys: displayKeysProp,
  onDisplayKeysChange,
  measurementSystem,
  displayUnitSystem,
  onDisplayUnitSystemChange,
  visible,
  onVisibilityChange,
  displayKeysPanelVisible,
  onToggleDisplayKeysPanel,
  allScales,
  onCopyForeignShape,
  onDrawSameShape,
  onRestoreDrawSettings,
  selectedTool,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = e.target.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === '*') {
        e.preventDefault();
        onVisibilityChange && onVisibilityChange(!visible);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, onVisibilityChange]);

  // Define calculated param keys that should be recomputed per shape (using translations)
  const CALCULATED_PARAM_KEYS = useMemo(
    () => [
      t('shapeList.total_length'),
      t('shapeList.total_area'),
      t('shapeList.diameter'),
    ],
    [t],
  );

  const getGroupingLabel = useCallback(
    (key) => {
      if (key === GROUP_BY_PAGE_KEY) return t('shapeList.page');
      return key;
    },
    [t],
  );

  const getMeasurementDataForShape = useCallback(
    (shape) => {
      const shapeScaleProps = resolveShapeScaleProps(
        shape,
        currentScaleProps,
        allScales,
      );
      const ctx = shape?.measurementContext;
      if (ctx && ctx.currentScaleProps && ctx.dpi) {
        return getShapeMeasurementData(
          shape,
          shapeScaleProps,
          ctx.dpi,
          ctx.imageSize || imageSize,
          displayUnitSystem,
        );
      }
      return getShapeMeasurementData(
        shape,
        shapeScaleProps,
        dpi,
        imageSize,
        displayUnitSystem,
      );
    },
    [currentScaleProps, dpi, imageSize, displayUnitSystem, allScales],
  );
  // --- dnd-kit Sensors ---
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
  );

  // --- Local Storage Keys ---
  const EXPANDED_SHAPES_KEY = `${storageKeyPrefix}replan_expandedShapes`;
  const ACTIVE_DETAIL_VIEW_KEY = `${storageKeyPrefix}replan_activeDetailView`;
  const VISIBLE_KEY = `${storageKeyPrefix}replan_shapeListVisible`;
  const GROUPING_KEY = `${storageKeyPrefix}replan_groupingKey`;
  const STACK_KEY = `${storageKeyPrefix}replan_stackIdentical`; // Key for local storage

  // --- State Initialization ---
  const [expandedShapes, setExpandedShapes] = useState(() => {
    const stored = localStorage.getItem(EXPANDED_SHAPES_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  const [activeDetailView, setActiveDetailView] = useState('params');

  // --- Assembly Save Status State ---
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [loadStatus, setLoadStatus] = useState('idle'); // 'idle', 'loading', 'loaded', 'error'
  const [projectAssemblies, setProjectAssemblies] = useState([]);
  // Use prop if provided, otherwise use empty string
  const groupingKey =
    groupingKeyProp !== undefined ? groupingKeyProp : '';
  const displayKeys =
    displayKeysProp !== undefined ? displayKeysProp : [];

  // Handle grouping key change
  const handleGroupingKeyChange = (newKey) => {
    if (onGroupingKeyChange) {
      onGroupingKeyChange(newKey);
    }
  };

  const [collapsedGroups, setCollapsedGroups] = useState(new Set()); // State for collapsed groups
  const [collapsedSelectionHeaders, setCollapsedSelectionHeaders] =
    useState(new Set(['unused_assemblies']));
  const [stackIdentical, setStackIdentical] = useState(() => {
    const stored = localStorage.getItem(STACK_KEY);
    return stored ? JSON.parse(stored) : false;
  }); // State for stacking identical items
  const [showSelectedOnly, setShowSelectedOnly] = useState(false); // <--- NEW

  const [assemblyManagerOpen, setAssemblyManagerOpen] =
    useState(false);

  const onOpenAssemblyManager = () => {
    setAssemblyManagerOpen(true);
  };

  const handleApplyAssembly = useCallback((assemblyParams) => {
    if (!selectedShapeIds || selectedShapeIds.length === 0) {
      alert(t('shapeList.select_shapes_prompt'));
      return;
    }
    selectedShapeIds.forEach((id) => {
      const shape = shapes.find((s) => s.id === id);
      const updatedParams = {
        ...(shape?.params || {}),
        ...(assemblyParams || {}),
      };
      onUpdateShape(id, { params: updatedParams });
    });
    setAssemblyManagerOpen(false);
  }, [selectedShapeIds, shapes, onUpdateShape, t]);

  const handleDeleteAssembly = async (assemblyId) => {
    if (!authUser || !firebase) {
      console.error('Not authenticated or Firebase not initialized.');
      return;
    }
    try {
      const organization =
        organizationProp || shapes[0]?.pageInfo?.organization;
      const actorUid = authUser.uid;
      const actorEmail =
        authUser.email || authUser.providerData?.[0]?.email || null;

      if (firebase.doDeleteAssembly) {
        await firebase.doDeleteAssembly(
          organization,
          projectName,
          assemblyId,
          actorUid,
          actorEmail,
        );
      } else if (
        firebase.assemblyDoc &&
        typeof firebase.assemblyDoc === 'function'
      ) {
        await firebase
          .assemblyDoc(organization, projectName, assemblyId)
          .delete();
        await firebase
          .maybeLogOrgActivity(organization, {
            action: 'ASSEMBLY_DELETED',
            actorUid,
            actorEmail,
            details: { projectName, assemblyId },
          })
          .catch(() => {});
      } else {
        console.warn(
          'No delete method available on firebase wrapper.',
        );
      }
      setProjectAssemblies((prev) =>
        prev.filter((a) => a.id !== assemblyId),
      );
    } catch (err) {
      console.error('Error deleting assembly:', err);
      alert(
        t('shapeList.failed_delete_assembly', { error: err.message }),
      );
    }
  };

  // --- Effects to Persist State ---
  useEffect(() => {
    localStorage.setItem(
      EXPANDED_SHAPES_KEY,
      JSON.stringify(Array.from(expandedShapes)),
    );
  }, [expandedShapes]);

  /*keep for later integration with local storage 
  useEffect(() => {
    localStorage.setItem(ACTIVE_DETAIL_VIEW_KEY, activeDetailView);
  }, [activeDetailView]);
  */

  useEffect(() => {
    localStorage.setItem(VISIBLE_KEY, JSON.stringify(visible));
  }, [visible]);

  useEffect(() => {
    localStorage.setItem(STACK_KEY, JSON.stringify(stackIdentical));
  }, [stackIdentical]);

  useEffect(() => {
    if (visible && authUser && firebase && projectName) {
      const fetchAssemblies = async () => {
        try {
          // Prefer the prop, fall back to shape pageInfo
          const organization =
            organizationProp || shapes[0]?.pageInfo?.organization;
          if (!organization) {
            console.warn(
              'No organization found, cannot fetch assemblies',
            );
            setProjectAssemblies([]);
            return;
          }
          const querySnapshot = await firebase.doGetProjectAssemblies(
            organization,
            projectName,
          );
          const assemblies = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setProjectAssemblies(assemblies);
        } catch (error) {
          console.error('Error fetching project assemblies:', error);
          setProjectAssemblies([]); // Clear on error
        }
      };
      fetchAssemblies();
    }
  }, [
    visible,
    authUser,
    firebase,
    projectName,
  ]);

  // --- Local fallback ordering when parent doesn't implement onReorderShapes
  const [localOrder, setLocalOrder] = useState(() =>
    shapes.map((s) => s.id),
  );
  // Keep localOrder in sync when shapes list changes (e.g. shapes added/removed)
  useEffect(() => {
    // If shape set changed or localOrder is empty, re-init to incoming shapes order
    const incomingIds = shapes.map((s) => s.id);
    if (
      !localOrder ||
      localOrder.length !== incomingIds.length ||
      !incomingIds.every((id) => localOrder.includes(id))
    ) {
      setLocalOrder(incomingIds);
    }
  }, [shapes]);

  // Build an ordered shapes array using localOrder when available
  const orderedShapes = useMemo(() => {
    if (localOrder && localOrder.length === shapes.length) {
      return localOrder
        .map((id) => shapes.find((s) => s.id === id))
        .filter(Boolean);
    }
    return shapes;
  }, [shapes, localOrder]);

  // NEW: Visible (filtered) ordered shapes (used everywhere below)
  const visibleOrderedShapes = useMemo(() => {
    if (!showSelectedOnly) return orderedShapes;
    const selSet = new Set(selectedShapeIds);
    return orderedShapes.filter((s) => selSet.has(s.id));
  }, [orderedShapes, showSelectedOnly, selectedShapeIds]);

  // --- Memoized Computations ---
  const allParamKeys = useMemo(() => {
    const keys = new Set();
    visibleOrderedShapes.forEach((shape) => {
      if (shape.params)
        Object.keys(shape.params).forEach((key) => keys.add(key));
    });
    return Array.from(keys).sort();
  }, [visibleOrderedShapes]);

  const hasPageInfo = useMemo(() => {
    return visibleOrderedShapes.some((s) => {
      const p = s?.pageInfo;
      return !!(
        p?.name ||
        p?.pageIndex !== undefined ||
        p?.index !== undefined
      );
    });
  }, [visibleOrderedShapes]);
  useEffect(() => {}, [visibleOrderedShapes]);

  // --- NEW: Memoized Display Items (Handles Stacking) ---
  const displayItems = useMemo(() => {
    const sourceShapes = visibleOrderedShapes;
    if (!stackIdentical) {
      return sourceShapes.map((shape) => {
        const measurementData = getMeasurementDataForShape(shape);
        return {
          id: shape.id,
          representativeShape: shape,
          shapeIds: [shape.id],
          count: 1,
          totalMeasurementSummary: measurementData.summary,
        };
      });
    }
    const getShapeStackKey = (shape) => {
      // For POINT shapes, include the marker type to prevent different markers stacking together
      const markerSuffix =
        shape.type === TOOLS.POINT
          ? `|marker:${shape.pointMarker || 'disk'}`
          : '';
      const isSel = selectedShapeIds.includes(shape.id);
      let key = `${shape.type}|${shape.stroke || 'no-stroke'}${markerSuffix}|sel:${isSel}`;

      if (groupingKey) {
        const groupValue =
          groupingKey === GROUP_BY_PAGE_KEY
            ? (shape?.pageInfo?.name ??
              shape?.pageInfo?.pageIndex ??
              shape?.pageInfo?.index ??
              'undefined')
            : (shape?.params?.[groupingKey] ?? 'undefined');
        key += `|${groupingKey}:${groupValue}`;
      }
      return key;
    };
    const stacks = new Map();
    sourceShapes.forEach((shape) => {
      const key = getShapeStackKey(shape);
      if (stacks.has(key)) {
        const existing = stacks.get(key);
        existing.shapeIds.push(shape.id);
        existing.count++;
      } else {
        stacks.set(key, {
          id: `stack-${key}`,
          representativeShape: shape,
          shapeIds: [shape.id],
          count: 1,
        });
      }
    });
    const finalStacks = Array.from(stacks.values());
    finalStacks.forEach((stack) => {
      const representativeMeasurement = getMeasurementDataForShape(
        stack.representativeShape,
      );
      if (stack.count > 1) {
        const repShape = stack?.representativeShape;
        const repScaleProps = resolveShapeScaleProps(
          repShape,
          currentScaleProps,
          allScales,
        );
        const repRatio = repScaleProps?.ratio;
        const repCtx = repShape?.measurementContext;
        const dpiForDisplay = repCtx?.dpi || dpi;
        const imageSizeForDisplay = repCtx?.imageSize || imageSize;
        const currentDisplayUnitSystem =
          repCtx?.displayUnitSystem || displayUnitSystem;
        const repIsArea = repShape?.type === TOOLS.AREA;

        let totalInBase = 0;
        let baseUnit = '';
        stack.shapeIds.forEach((id) => {
          const s = sourceShapes.find((ss) => ss.id === id);
          if (s) {
            const data = getMeasurementDataForShape(s);
            if (data.rawValue > 0) {
              const shapeScaleProps = resolveShapeScaleProps(
                s,
                currentScaleProps,
                allScales,
              );
              const shapeRatio = shapeScaleProps?.ratio;
              const linearFactor =
                repRatio && shapeRatio ? shapeRatio / repRatio : 1;
              const conversionFactor = repIsArea
                ? linearFactor * linearFactor
                : linearFactor;
              totalInBase += data.rawValue * conversionFactor;
              if (!baseUnit) baseUnit = data.rawUnit;
            }
          }
        });
        const isArea = baseUnit.includes('^2');

        const totalDisplay = isArea
          ? getDisplayAreaMeasurement(
              totalInBase,
              repScaleProps,
              dpiForDisplay,
              currentDisplayUnitSystem,
            )
          : getDisplayMeasurement(
              totalInBase,
              repScaleProps,
              dpiForDisplay,
              imageSizeForDisplay,
              currentDisplayUnitSystem,
            );

        const summaryLabel =
          representativeMeasurement.summary.split(':')[0] ||
          (isArea
            ? t('shapeList.total_area')
            : t('shapeList.total_length'));
        stack.totalMeasurementSummary = `${summaryLabel}: ${totalDisplay.value} ${totalDisplay.unit}`;
      } else {
        stack.totalMeasurementSummary =
          representativeMeasurement.summary;
      }
    });
    // Sort: selected stacks (sel:true) come first
    finalStacks.sort((a, b) => {
      const aSel = a.id.includes('|sel:true');
      const bSel = b.id.includes('|sel:true');
      if (aSel && !bSel) return -1;
      if (!aSel && bSel) return 1;
      return 0;
    });
    // Insert separator between selected and non-selected
    const sepIdx = finalStacks.findIndex(s => !s.id.includes('|sel:true'));
    if (sepIdx > 0) {
      finalStacks.splice(sepIdx, 0, { id: '__sel-separator__', _separator: true });
    }
    return finalStacks;
  }, [
    visibleOrderedShapes,
    stackIdentical,
    groupingKey,
    selectedShapeIds,
    currentScaleProps,
    dpi,
    imageSize,
    displayUnitSystem,
    getMeasurementDataForShape,
    t,
  ]); // <-- Use orderedShapes

  const orphanAssemblies = useMemo(() => {
    if (!projectAssemblies?.length) return [];
    const usedNames = new Set();
    displayItems.forEach((item) => {
      const name = item.representativeShape?.params?.Assembly;
      if (name) usedNames.add(name);
    });
    return projectAssemblies.filter((a) => !usedNames.has(a.name));
  }, [projectAssemblies, displayItems]);

  const groupedShapesData = useMemo(() => {
    if (!groupingKey) return null;

    const groups = {};
    const sortMeta = new Map(); // groupName -> sortKey

    const getPageIndex = (shape) => {
      const idx =
        shape?.pageInfo?.pageIndex ?? shape?.pageInfo?.index;
      return typeof idx === 'number' ? idx : null;
    };

    const getGroupName = (shape) => {
      if (groupingKey === GROUP_BY_PAGE_KEY) {
        const name = shape?.pageInfo?.name;
        if (name) return name;
        const idx = getPageIndex(shape);
        if (idx !== null) return `${t('shapeList.page')} ${idx + 1}`;
        return t('shapeList.not_set');
      }
      return shape?.params?.[groupingKey] || t('shapeList.not_set');
    };

    displayItems.forEach((item) => {
      const shape = item.representativeShape;
      const groupName = getGroupName(shape);

      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(item);

      if (!sortMeta.has(groupName)) {
        const sortKey =
          groupingKey === GROUP_BY_PAGE_KEY
            ? (getPageIndex(shape) ?? Number.POSITIVE_INFINITY)
            : String(groupName).toLowerCase();
        sortMeta.set(groupName, sortKey);
      }
    });

    const order = Object.keys(groups).sort((a, b) => {
      if (groupingKey === GROUP_BY_PAGE_KEY) {
        const sa = sortMeta.get(a);
        const sb = sortMeta.get(b);
        if (Number.isFinite(sa) && Number.isFinite(sb) && sa !== sb)
          return sa - sb;
      }
      return a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });

    return { groups, order };
  }, [displayItems, groupingKey, t]);

  const groupedShapes = groupedShapesData?.groups || null;
  const groupedShapeOrder = groupedShapesData?.order || [];

  const allItemIds = useMemo(() => {
    if (groupedShapesData) {
      const { groups, order } = groupedShapesData;
      return order.flatMap((groupName) => {
        if (collapsedGroups.has(groupName)) return [];
        return (groups[groupName] || []).filter(i => !i._separator).map((i) => i.id);
      });
    }
    return displayItems.filter(i => !i._separator).map((i) => i.id);
  }, [displayItems, groupedShapesData, collapsedGroups]);

  // Track active drag item for overlay
  const [activeDragItem, setActiveDragItem] = useState(null);

  const handleDragStart = (event) => {
    const { active } = event;
    const activeId = String(active.id).replace(
      /__(selected|not_selected)$/,
      '',
    );
    const item = displayItems.find((i) => i.id === activeId);
    if (item) setActiveDragItem(item);
  };

  const handleDragCancel = () => {
    setActiveDragItem(null);
  };

  const handleDragEndWrapper = (event) => {
    handleDragEnd(event);
    setActiveDragItem(null);
  };

  // --- Handlers ---
  const handleDragEnd = (event) => {
    if (showSelectedOnly) return; // Disable reorder while filtered
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Strip selection suffix from split stack IDs
    const activeId = String(active.id).replace(
      /__(selected|not_selected)$/,
      '',
    );
    const overId = String(over.id).replace(
      /__(selected|not_selected)$/,
      '',
    );

    // Helper: apply new ordered shape IDs to parent or local
    const applyNewOrder = (newOrderShapeIds) => {
      if (typeof onReorderShapes === 'function') {
        onReorderShapes(newOrderShapeIds);
      } else {
        setLocalOrder(newOrderShapeIds);
      }
    };

    // --- CASE 1: No grouping -> flat reorder of displayItems
    if (!groupingKey) {
      const fromIndex = displayItems.findIndex(
        (i) => i.id === activeId,
      );
      const toIndex = displayItems.findIndex((i) => i.id === overId);
      if (
        fromIndex !== -1 &&
        toIndex !== -1 &&
        fromIndex !== toIndex
      ) {
        const newDisplay = [...displayItems];
        const [moved] = newDisplay.splice(fromIndex, 1);
        newDisplay.splice(toIndex, 0, moved);
        applyNewOrder(newDisplay.flatMap((i) => i.shapeIds));
        return;
      }
    }

    // Utility: find group name for an item id (stack id or shape id)
    const findGroupForItemId = (itemId) => {
      if (!groupedShapes) return null;
      for (const gName in groupedShapes) {
        const items = groupedShapes[gName];
        if (
          items.some(
            (it) => it.id === itemId || it.shapeIds.includes(itemId),
          )
        ) {
          return gName;
        }
      }
      return null;
    };

    // --- CASE 2: Grouped: reorder inside same group
    if (groupingKey && groupedShapes) {
      const activeGroup = findGroupForItemId(activeId);
      const overGroup = findGroupForItemId(overId);

      if (activeGroup && overGroup && activeGroup === overGroup) {
        const groupItems = groupedShapes[activeGroup];
        const fromIndex = groupItems.findIndex(
          (i) => i.id === activeId,
        );
        const toIndex = groupItems.findIndex((i) => i.id === overId);
        if (
          fromIndex !== -1 &&
          toIndex !== -1 &&
          fromIndex !== toIndex
        ) {
          const reorderedGroup = [...groupItems];
          const [moved] = reorderedGroup.splice(fromIndex, 1);
          reorderedGroup.splice(toIndex, 0, moved);

          // Reconstruct full display order (groups stay in their original overall order)
          const newDisplayOrder = [];
          Object.keys(groupedShapes)
            .sort()
            .forEach((gName) => {
              if (gName === activeGroup) {
                newDisplayOrder.push(...reorderedGroup);
              } else {
                newDisplayOrder.push(...groupedShapes[gName]);
              }
            });

          applyNewOrder(newDisplayOrder.flatMap((i) => i.shapeIds));
          return;
        }
      }
    }

    // --- CASE 3: Cross-group drop => change group param for ALL shapes in the dragged stack ---
    // Find the displayItem representing the dragged item (stack or single)
    const activeItem = displayItems.find(
      (it) => it.id === activeId || it.shapeIds.includes(activeId),
    );
    const targetShapeIds = activeItem
      ? activeItem.shapeIds
      : [activeId];

    // Normalize active id to actual shape id for lookups
    let activeShapeId = activeId;
    if (
      typeof activeId === 'string' &&
      activeId.startsWith('stack-')
    ) {
      const activeItemFallback = displayItems.find(
        (item) => item.id === activeId,
      );
      if (
        activeItemFallback &&
        activeItemFallback.shapeIds.length > 0
      ) {
        activeShapeId = activeItemFallback.shapeIds[0];
      }
    }

    const activeShape = orderedShapes.find(
      (s) => s.id === activeShapeId,
    );
    let newGroupName = null;

    if (over.data.current?.type === 'group') {
      newGroupName = over.data.current.groupName;
    } else if (groupedShapes) {
      newGroupName = findGroupForItemId(overId);
    }

    if (activeShape && newGroupName !== null && groupingKey) {
      if (groupingKey === GROUP_BY_PAGE_KEY) return; // Page grouping is read-only
      const currentGroupValue =
        activeShape.params?.[groupingKey] || 'Not Set';
      if (currentGroupValue !== newGroupName) {
        const updatedValue =
          newGroupName === 'Not Set' ? null : newGroupName;
        // Apply update to every shape id in the dragged stack
        targetShapeIds.forEach((id) => {
          const shapeForId =
            orderedShapes.find((s) => s.id === id) || activeShape;
          const updatedParams = {
            ...(shapeForId.params || {}),
            [groupingKey]: updatedValue,
          };
          onUpdateShape(id, { params: updatedParams });
        });
      }
    }
  };

  const toggleGroupCollapse = (groupName) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const toggleExpanded = (shapeId) => {
    const newExpanded = new Set(expandedShapes);
    if (newExpanded.has(shapeId)) {
      newExpanded.delete(shapeId);
    } else {
      newExpanded.add(shapeId);
    }
    setExpandedShapes(newExpanded);
  };

  const handleSaveAsAssembly = async (shape, assemblyName) => {
    if (!authUser || !firebase) {
      console.error('Not authenticated or Firebase not initialized.');
      return;
    }

    // Use provided assemblyName parameter first, fallback to shape params or prompt
    const finalAssemblyName =
      assemblyName ||
      shape.params?.Assembly ||
      shape.params?.[t('shapeList.assembly')] ||
      shape.name ||
      prompt(t('shapeList.enter_assembly_name'));
    if (!finalAssemblyName) {
      return; // User cancelled or entered an empty name
    }

    setSaveStatus('saving');

    const organization =
      organizationProp || shapes[0]?.pageInfo?.organization;
    const actorUid = authUser.uid;
    const actorEmail =
      authUser.email || authUser.providerData?.[0]?.email || null;

    // Use the shared helper which handles validation, param building, and update-on-conflict
    const result = await saveAssemblyFromShape({
      firebase,
      organization,
      projectName,
      shape: {
        ...shape,
        params: {
          ...(shape.params || {}),
          Assembly: finalAssemblyName,
        },
      },
      getMeasurementDataForShape,
      actorUid,
      actorEmail,
      t,
      buildOptions: {
        getCalculatedKey: (key) => {
          // Use translated keys to match existing assembly storage convention
          const map = {
            'Total Length': t('shapeList.total_length'),
            'Total Area': t('shapeList.total_area'),
            Diameter: t('shapeList.diameter'),
          };
          return map[key] || key;
        },
      },
    });

    if (result.success) {
      setSaveStatus('saved');
      console.log(
        t('shapeList.assembly_saved', { name: finalAssemblyName }),
      );
    } else if (result.status === 'cancelled') {
      setSaveStatus('idle');
    } else {
      setSaveStatus('error');
    }

    setTimeout(
      () => setSaveStatus('idle'),
      result.status === 'cancelled' ? 0 : 2000,
    );
  };

  const handleLoadAssembly = useCallback(
    async (shape, assemblyName, targetShapeIds = [shape.id]) => {
      if (!authUser || !firebase) {
        console.error(
          'Not authenticated or Firebase not initialized.',
        );
        return;
      }

      if (!assemblyName) {
        alert(t('shapeList.assembly_name_required'));
        return;
      }

      setLoadStatus('loading');

      try {
        const organization =
          organizationProp || shapes[0]?.pageInfo?.organization;

        // Resolve friendly assembly name to actual document ID if possible
        const matchedAssembly = projectAssemblies.find(
          (a) => a.name === assemblyName || a.id === assemblyName,
        );
        const resolvedId = matchedAssembly
          ? matchedAssembly.id
          : assemblyName;

        const assemblyDoc = await firebase
          .assemblyDoc(organization, projectName, resolvedId)
          .get();

        if (!assemblyDoc.exists) {
          alert(
            t('shapeList.assembly_not_found', { name: assemblyName }),
          );
          setLoadStatus('error');
          return;
        }

        const assemblyData = assemblyDoc.data();

        // Filter out calculated params from the assembly data
        // Update each target shape's parameters with the loaded assembly data
        targetShapeIds.forEach((id) => {
          const shapeForId = shapes.find((s) => s.id === id) || shape;

          // Filter out calculated params from the assembly data
          const assemblyParams = assemblyData.params || {};
          const filteredAssemblyParams = Object.keys(
            assemblyParams,
          ).reduce((acc, key) => {
            if (!CALCULATED_PARAM_KEYS.includes(key)) {
              acc[key] = assemblyParams[key];
            }
            return acc;
          }, {});

          const updatedParams = {
            Assembly: resolvedId,
            ...(shapeForId.params?.Reference !== undefined
              ? { Reference: shapeForId.params.Reference }
              : {}),
            ...(shapeForId.params?.Color !== undefined
              ? { Color: shapeForId.params.Color }
              : {}),
            ...filteredAssemblyParams,
          };
          onUpdateShape(id, { params: updatedParams });
        });

        setLoadStatus('loaded');
        console.log(
          t('shapeList.assembly_loaded', { name: assemblyName }),
        );
      } catch (error) {
        console.error('Error loading assembly:', error);
        setLoadStatus('error');
        alert(
          t('shapeList.failed_load_assembly', {
            error: error.message,
          }),
        );
      } finally {
        setTimeout(() => setLoadStatus('idle'), 2000); // Reset after 2 seconds
      }
    },
    [
      authUser,
      firebase,
      projectName,
      onUpdateShape,
      shapes,
      organizationProp,
      t,
      CALCULATED_PARAM_KEYS,
      projectAssemblies,
    ],
  );

  // --- MODIFIED: renderShapeList now iterates over `displayItems` ---
  const renderShapeList = (itemsToRender) => {
    return itemsToRender.map((item) => {
      if (item._separator) {
        return <div key="__sel-separator__" className="horizontal-separator shapelist-separator-centered" />;
      }
      const {
        representativeShape,
        count,
        shapeIds,
        totalMeasurementSummary,
      } = item;
      // Determine selection state for the item/stack
      const selectedCount = shapeIds.filter((id) =>
        selectedShapeIds.includes(id),
      ).length;
      const isSelected = selectedCount > 0;
      const isIndeterminate =
        selectedCount > 0 && selectedCount < count;

      // When partially selected, target only the selected shapes;
      // otherwise (fully selected or none selected), target the whole stack.
      const effectiveTargetShapeIds =
        isIndeterminate
          ? shapeIds.filter((id) => selectedShapeIds.includes(id))
          : shapeIds;

      return (
        <DraggableShapeListItem
          projectAssemblies={projectAssemblies}
          firebase={firebase}
          key={item.id}
          id={item.id} // Pass id for dnd-kit
          shapeIds={shapeIds} // <-- pass the underlying shape IDs so DroppableGroup can select them
          shape={representativeShape} // The shape to display info for
          count={count} // The number of items in the stack
          sourceVersion={representativeShape.sourceVersion}
          onCopyForeignShape={onCopyForeignShape}
          measurementData={{
            ...getMeasurementDataForShape(representativeShape),
            summary: totalMeasurementSummary,
          }} // Pass the new total summary
          isSelected={isSelected}
          isIndeterminate={isIndeterminate}
          isExpanded={expandedShapes.has(item.id)}
          activeDetailView={activeDetailView}
          onShapeSelect={() => {
            // If not all are selected, select all. Otherwise, deselect all.
            const shouldSelectAll = selectedCount < count;
            onShapeSelect(shapeIds, shouldSelectAll);
          }}
          onToggleExpand={() =>
            toggleExpanded(item.id)
          }
          onUpdateShape={onUpdateShape} // Note: ShapeListItem will now propagate when shapeIds are passed
          onSaveAsAssembly={(assemblyName) =>
            handleSaveAsAssembly(representativeShape, assemblyName)
          }
          onLoadAssembly={(assemblyName) =>
            handleLoadAssembly(
              representativeShape,
              assemblyName ||
                representativeShape.params?.Assembly ||
                representativeShape.params?.[t('shapeList.assembly')],
              effectiveTargetShapeIds,
            )
          }
          saveStatus={saveStatus}
          loadStatus={loadStatus}
          stackIdentical={stackIdentical} // <-- tell item whether stacking is enabled
          onDrawSameShape={onDrawSameShape}
          onRestoreDrawSettings={onRestoreDrawSettings}
          selectedTool={selectedTool}
        />
      );
    });
  };

  return (
    <div className={`ShapeList ${visible ? 'open' : 'closed'}`}>
      <div
        className="shapelist-header-actions"
        style={{
          padding: visible ? '2px' : '0',
        }}
      >
        {visible && (
          <div className="shapelist-header">
            {/* --- Detail View Selector (currently keep it commented out) --- */}
            <select
              className="detail-view-controls"
              value={activeDetailView}
              onChange={(e) => setActiveDetailView(e.target.value)}
              data-tt={`${t('shapeList.detail_view_desc')}\n${t('shapeList.detail_view_title')}\n${t('common.click_change', { defaultValue: 'Change' })}`}
            >
              <option value="params">{t('shapeList.params')}</option>
              <option value="measurements">
                {t('shapeList.measures')}
              </option>
              <option value="tags">{t('shapeList.tags')}</option>
              <option value="vertices">
                {t('shapeList.vertices')}
              </option>
            </select>
            {(allParamKeys.length > 0 || hasPageInfo) && (
              <select
                className="shapelist-detail-view-controls shapelist-grouping-select"
                value={groupingKey}
                onChange={(e) =>
                  handleGroupingKeyChange(e.target.value)
                }
                data-tt={`${t('shapeList.group_by_param_desc')}\n${t('shapeList.group_by_param_title')}\n${t('common.click_change', { defaultValue: 'Change' })}`}
              >
                <option value="">{t('shapeList.no_grouping')}</option>
                {hasPageInfo && (
                  <option value={GROUP_BY_PAGE_KEY}>
                    {t('shapeList.by')}:{' '}
                    {getGroupingLabel(GROUP_BY_PAGE_KEY)}
                  </option>
                )}
                {allParamKeys.map((key) => (
                  <option key={key} value={key}>
                    {t('shapeList.by')}: {key}
                  </option>
                ))}
              </select>
            )}

            {/* --- Stacking Checkbox --- */}
            <input
              className="shapelist-detail-view-assembly"
              type="checkbox"
              checked={stackIdentical}
              onChange={(e) => setStackIdentical(e.target.checked)}
              data-tt={`${t('shapeList.stack_desc')}\n${t('shapeList.stack_title')}\n${t('common.click_toggle', { defaultValue: 'Toggle' })}`}
            />
            {/* --- Selected Only Checkbox --- */}
            {/* keep commented out for now, can be re-enabled later if needed */}
            {/* <input
              className="shapelist-detail-view-selected-only"
              type="checkbox"
              checked={showSelectedOnly}
              onChange={(e) => setShowSelectedOnly(e.target.checked)}
              data-tt={`${t("shapeList.show_selected_desc", { defaultValue: "Filter the shape list to show only selected shapes" })}\n${t("shapeList.show_selected_title")}\n${t("common.click_toggle", { defaultValue: "Toggle" })}`}
            /> */}
            {/* --- Display Keys Panel Toggle --- */}
            <button
              onClick={onToggleDisplayKeysPanel}
              className={`assembly-manager-button ${displayKeysPanelVisible ? 'transform-active' : ''}`}
              data-tt={`${t('shapeList.display_settings_desc')}\n${displayKeysPanelVisible ? t('shapeList.hide_display_settings') : t('shapeList.show_display_settings')}\n${t('common.shortcut', { key: 'D', defaultValue: 'D' })}`}
              type="button"
              style={{ marginLeft: '150px' }}
            >
              <TagIcon className="tool-icon stroke-icon" />
            </button>
            <button
              onClick={onOpenAssemblyManager}
              className="assembly-manager-button"
              data-tt={`${t('shapeList.assembly_manager_title_desc')}\n${t('shapeList.assembly_manager_title')}\n${t('common.click_open', { defaultValue: 'Open' })}`}
            >
              <SettingsIcon className="tool-icon stroke-icon" />
            </button>
          </div>
        )}
        <button
          className="shapelist-toggle-button"
          onClick={() =>
            onVisibilityChange && onVisibilityChange(!visible)
          }
          data-tt={`${t('shapeList.shape_list_desc')}\n${visible ? t('shapeList.hide_shape_list') : t('shapeList.show_shape_list')}\n${t('common.shortcut', { key: '*', defaultValue: '*' })}`}
        >
          {visible ? (
            <SidebarOpen className="tool-icon" />
          ) : (
            <SidebarClose className="tool-icon" />
          )}
        </button>
      </div>
      {visible && (
        <div className="horizontal-separator shapelist-separator-centered" />
      )}
      {visible && (
        <div className="shapelist-content-scrollable">
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEndWrapper}
          >
            <SortableContext
              items={allItemIds}
              strategy={verticalListSortingStrategy}
            >
              {groupedShapesData ? (
                groupedShapeOrder.map((groupName) => {
                  const itemsInGroup = groupedShapes[groupName] || [];
                  const isAssemblyGrouping =
                    groupingKey === t('shapeList.assembly') ||
                    groupingKey === 'Assembly';
                  const assemblyData = isAssemblyGrouping
                    ? projectAssemblies.find(
                        (a) =>
                          a.id === groupName || a.name === groupName,
                      )
                    : null;
                  const children = (
                    <>
                      {(() => {
                        const selItems = itemsInGroup.filter(d => d.id.includes('|sel:true'));
                        const nonSelItems = itemsInGroup.filter(d => !d.id.includes('|sel:true') && !d._separator);
                        return (
                          <>
                            {!collapsedSelectionHeaders.has('selected') && renderShapeList(selItems)}
                            {selItems.length > 0 && nonSelItems.length > 0 && (
                              <div className="horizontal-separator shapelist-separator-light" />
                            )}
                            {!collapsedSelectionHeaders.has('not_selected') && renderShapeList(nonSelItems)}
                          </>
                        );
                      })()}
                    </>
                  );
                  return (
                    <DroppableGroup
                      key={groupName}
                      groupName={groupName}
                      groupingKey={groupingKey}
                      groupingKeyLabel={getGroupingLabel(groupingKey)}
                      groupShapeIds={itemsInGroup.flatMap(
                        (item) => item.shapeIds,
                      )}
                      itemCount={itemsInGroup.reduce(
                        (sum, item) => sum + item.count,
                        0,
                      )}
                      isCollapsed={collapsedGroups.has(groupName)}
                      onToggleCollapse={() =>
                        toggleGroupCollapse(groupName)
                      }
                      selectedShapeIds={selectedShapeIds}
                      onShapeSelect={onShapeSelect}
                      isAssemblyGrouping={isAssemblyGrouping}
                      assemblyData={assemblyData}
                    >
                      {!collapsedGroups.has(groupName) && children}
                    </DroppableGroup>
                  );
                })
              ) : (
                <>
                  {(() => {
                    const selItems = displayItems.filter(d => d.id.includes('|sel:true'));
                    const nonSelItems = displayItems.filter(d => !d.id.includes('|sel:true') && !d._separator);
                    const hasBoth = selItems.length > 0 && nonSelItems.length > 0;
                    return (
                      <>
                        {selItems.length > 0 && (
                          <span
                            onClick={() => setCollapsedSelectionHeaders(prev => {
                              const next = new Set(prev);
                              next.has('selected') ? next.delete('selected') : next.add('selected');
                              return next;
                            })}
                            className="shape-list-selection-toggle"
                          >
                            {collapsedSelectionHeaders.has('selected') ? '➕ ' : '➖ '}
                            <span className="group-label">
                              {t('shapeList.selected')} ({selItems.reduce((s, d) => s + d.count, 0)})
                            </span>
                          </span>
                        )}
                        {!collapsedSelectionHeaders.has('selected') && renderShapeList(selItems)}
                        {hasBoth && (
                          <div className="horizontal-separator shapelist-separator-centered" />
                        )}
                        {nonSelItems.length > 0 && (
                          <span
                            onClick={() => setCollapsedSelectionHeaders(prev => {
                              const next = new Set(prev);
                              next.has('not_selected') ? next.delete('not_selected') : next.add('not_selected');
                              return next;
                            })}
                            className="shape-list-selection-toggle"
                          >
                            {collapsedSelectionHeaders.has('not_selected') ? '➕ ' : '➖ '}
                            <span className="group-label">
                              {t('shapeList.not_selected')} ({nonSelItems.reduce((s, d) => s + d.count, 0)})
                            </span>
                          </span>
                        )}
                        {!collapsedSelectionHeaders.has('not_selected') && renderShapeList(nonSelItems)}
                      </>
                    );
                  })()}
                </>
              )}
            </SortableContext>
            {stackIdentical && orphanAssemblies.length > 0 && (
              <div className="horizontal-separator shapelist-separator-centered" />
            )}
            {stackIdentical && orphanAssemblies.length > 0 && (
              <div className="shapelist-orphan-assemblies">
                <span
                  onClick={() => {
                    setCollapsedSelectionHeaders((prev) => {
                      const next = new Set(prev);
                      if (next.has('unused_assemblies'))
                        next.delete('unused_assemblies');
                      else next.add('unused_assemblies');
                      return next;
                    });
                  }}
                  className="shape-list-selection-toggle"
                >
                  {collapsedSelectionHeaders.has('unused_assemblies')
                    ? '➕ '
                    : '➖ '}
                  <span className="group-label">
                    {t(
                      'shapeList.unused_assemblies',
                      'Unused assemblies',
                    )}{' '}
                    ({orphanAssemblies.length})
                  </span>
                </span>
                {!collapsedSelectionHeaders.has(
                  'unused_assemblies',
                ) && (
                  <div className="shapelist-orphan-list">
                    {orphanAssemblies.map((assembly) => (
                      <div
                        key={assembly.id || assembly.name}
                        className="shapelist-orphan-item"
                        style={{
                          borderLeft: assembly.params?.Color
                            ? `8px solid ${assembly.params.Color}`
                            : undefined,
                        }}
                      >
                        {' '}
                        <span className="shapelist-orphan-name">
                          {assembly.name}
                        </span>
                        <button
                          className="shapelist-orphan-apply-btn"
                          data-tt={`${t('shapeList.apply_to_selected_desc')}\n${t('shapeList.apply_to_selected', 'Apply to selected shapes')}\n${t('common.click_activate', { defaultValue: 'Activate' })}`}
                          onClick={() =>
                            handleApplyAssembly(assembly.params)
                          }
                          disabled={!selectedShapeIds?.length}
                        >
                          {t('shapeList.apply', 'Apply')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <DragOverlay dropAnimation={null}>
              {activeDragItem && (
                <div className="shapelist-drag-overlay">
                  {/*
                  Re-use the visual part only (not DraggableShapeListItem because that re-registers useSortable).
                  Minimal clone:
                */}
                  {renderShapeList([activeDragItem])}
                </div>
              )}
            </DragOverlay>
          </DndContext>
        </div>
      )}
      {assemblyManagerOpen &&
        ReactDOM.createPortal(
          <div
            className="assembly-modal-overlay"
            onClick={() => setAssemblyManagerOpen(false)}
          >
            <div
              className="assembly-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <AssemblyManager
                firebase={firebase}
                projectName={projectName}
                organizationId={
                  organizationProp ||
                  shapes[0]?.pageInfo?.organization
                }
                authUser={authUser}
                onAssembliesChange={setProjectAssemblies}
                onClose={() => setAssemblyManagerOpen(false)}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ShapeList;
